/* eslint-disable react/no-danger */
// `__Ashby` is the global exposed by Ashby's embed script; its name is fixed by
// their API, so we allow the dangling underscores here.
/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';

import BpkLargeCityCenterIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/city-center';
import DomPurify from 'dompurify';

import {
  formatAshbyJobLocations,
  useGetSingleAshbyJob,
} from '../hooks/useGetJobs';
import {
  ASHBY_EMBED_SCRIPT_URL,
  ASHBY_JOB_BOARD_URL,
} from '../utils/constants';

interface JobPageProps {
  jobsSlug: string;
}

const AshbyJobPage = ({ jobsSlug }: JobPageProps) => {
  const jobsSlugParts = jobsSlug.split('/');
  const jobId = jobsSlugParts[jobsSlugParts.length - 1];
  const { job, loading } = useGetSingleAshbyJob(jobId);
  const [formOpen, setFormOpen] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);

  useEffect(() => {
    if (!formOpen || typeof window === 'undefined') {
      return undefined;
    }

    // Clear any prior failure on each fresh open attempt so reopening retries.
    setEmbedFailed(false);

    const settings = {
      // Drive the embed manually: this is a SPA, so we render the iframe
      // ourselves once the script is ready rather than on the page-load event.
      autoLoad: false,
      autoScroll: false,
      ashbyBaseJobBoardUrl: ASHBY_JOB_BOARD_URL,
      displayMode: 'application-form-only',
      jobPostingId: jobId,
    };

    // The real Ashby embed API exposes iFrame()/initialize(). We pre-seed a
    // `{ settings }` placeholder on window.__Ashby before the script loads, so a
    // truthy window.__Ashby does NOT mean the API is ready — check for the method.
    const isAshbyReady = (app: any) =>
      Boolean(app) && typeof app.iFrame === 'function';

    // Render the application form into the #ashby_embed container.
    const renderForm = () => {
      const ashbyApp = (window as any).__Ashby;
      if (!isAshbyReady(ashbyApp)) {
        return;
      }
      Object.assign(ashbyApp.settings, settings);
      // The script auto-initializes on the page-load event, which has already
      // fired in a SPA, so initialize explicitly if it hasn't happened yet.
      if (!ashbyApp.isInitialized) {
        ashbyApp.initialize();
      }
      ashbyApp.iFrame().load();
    };

    const existingApp = (window as any).__Ashby;

    // Script already loaded and API ready (e.g. reopening the form) → re-render.
    if (isAshbyReady(existingApp)) {
      renderForm();
      return undefined;
    }

    // Not ready yet. Seed/refresh the pending settings so the script initializes
    // into application-form-only mode instead of briefly rendering the full board.
    if (existingApp) {
      Object.assign(existingApp.settings, settings);
    } else {
      (window as any).__Ashby = { settings };
    }

    // Attach a load handler that renders once the API is ready. Reuse an existing
    // script tag if one was already injected (a previous open may have removed its
    // handler on close), otherwise inject the script now. This avoids both a
    // premature method call on the placeholder and a lost render on reopen.
    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${ASHBY_EMBED_SCRIPT_URL}"]`,
    );
    if (!script) {
      script = document.createElement('script');
      script.src = ASHBY_EMBED_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
    const scriptEl = script;

    // If the script fails to load (network error, ad-blocker, CSP), remove it so
    // a later reopen injects a fresh one instead of re-listening to a dead tag,
    // and flag the failure so we can show a direct apply link as a fallback.
    const handleError = () => {
      scriptEl.remove();
      setEmbedFailed(true);
    };

    scriptEl.addEventListener('load', renderForm);
    scriptEl.addEventListener('error', handleError);
    return () => {
      scriptEl.removeEventListener('load', renderForm);
      scriptEl.removeEventListener('error', handleError);
    };
  }, [formOpen, jobId]);

  const handleClickApply = () => {
    setFormOpen((prevFormOpen) => !prevFormOpen);
  };

  /* The purpose of this method is to let Talent include metadata for LinkedIn as
   * regular text in the job description section, but to hide it (by making it white) from anyone
   * viewing said job.
   *
   * Example tags: '#LI-KB1', '#LI-Hybrid'.
   */
  const hideLinkedInTag = (content: string) =>
    content.replaceAll(
      '\u003E#LI-',
      ' id="linkedin-tag" class="jobs__linkedin-tag"\u003E#LI-',
    );

  const decodeHTMLEntities = (result: string) => {
    const e = document.createElement('textarea');
    e.innerHTML = result;
    return e.value;
  };

  const getJobDescriptionHTML = (content: string) => {
    const result = hideLinkedInTag(content);
    const html = decodeHTMLEntities(result);
    return DomPurify.sanitize(html);
  };

  return (
    <section className="grid__container">
      {loading && <div />}
      {!loading && job === null && (
        <div className="job-unavailable-msg">
          This job is no longer available.
        </div>
      )}
      {!loading && job && (
        <div className="grid__row content-block">
          <div className="grid__column--12 grid__column--mobile-12">
            {job.department && (
              <p className="page-sub-title">{job.department}</p>
            )}
            <h1 className="post-title">{job.title}</h1>
            {job.location && (
              <p className="post-location sub-heading">
                <BpkLargeCityCenterIcon className="city-center-icon" />
                {formatAshbyJobLocations(job)}
              </p>
            )}
            <div
              id="job-description"
              dangerouslySetInnerHTML={{
                __html: getJobDescriptionHTML(job.descriptionHtml),
              }}
            />
            <button
              id="apply"
              className="job_apply"
              type="button"
              onClick={handleClickApply}
            >
              {formOpen ? 'Cancel' : 'Apply Now'}
            </button>
          </div>
        </div>
      )}
      <div
        className="greenhouse-wrapper"
        style={{ display: formOpen ? 'inherit' : 'none' }}
      >
        {embedFailed ? (
          <p className="job-apply-fallback">
            We could not load the application form.{' '}
            {job?.applyUrl && (
              <a href={job.applyUrl} target="_blank" rel="noopener noreferrer">
                Apply on our careers site
              </a>
            )}
          </p>
        ) : (
          <div id="ashby_embed" />
        )}
      </div>
    </section>
  );
};

export default AshbyJobPage;
